import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Pencil, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ConvertModal } from "@/components/ConvertModal";
import { waLink, STUDENT_STATUS_LABELS, ENTITY_STATUS_CLS } from "@/lib/prospectMeta";

function Row({ label, value }) {
  if (!value) return null;
  return <div className="py-1.5 border-b border-slate-100 last:border-0"><p className="text-xs text-slate-400">{label}</p><p className="text-sm text-slate-800">{value}</p></div>;
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/students/${id}`); setS(data); }
    catch { toast.error("Data siswa tidak ditemukan."); navigate("/siswa-klien"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {}); }, []);

  if (loading || !s) return <AppLayout><div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AppLayout>;

  return (
    <AppLayout>
      <button onClick={() => navigate("/siswa-klien")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="student-back"><ArrowLeft className="h-4 w-4" /> Kembali ke Siswa & Klien</button>
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="student-name">{s.full_name}</h1>
            <p className="text-sm text-slate-500 font-mono">{s.student_no}</p>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs border ${ENTITY_STATUS_CLS[s.status] || "bg-slate-100"}`} data-testid="student-status">{STUDENT_STATUS_LABELS[s.status] || s.status}</span>
          </div>
          <div className="flex gap-2">
            <a href={waLink(s.phone_normalized)} target="_blank" rel="noreferrer" data-testid="student-wa" className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <button onClick={() => setShowEdit(true)} data-testid="student-edit" className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-50"><Pencil className="h-4 w-4" /> Edit</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <Row label="WhatsApp" value={s.phone_normalized} />
          <Row label="Email" value={s.email} />
          <Row label="Nama Orang Tua/Wali" value={s.guardian_name} />
          <Row label="Sekolah/Univ/Perusahaan" value={s.school_or_company} />
          <Row label="Kelas/Pekerjaan" value={s.class_or_job} />
          <Row label="Alamat" value={s.address} />
          <Row label="Program" value={s.program} />
          <Row label="Tujuan Belajar" value={s.learning_goal} />
          <Row label="Metode" value={s.mode} />
          <Row label="Jadwal Diinginkan" value={s.desired_schedule} />
          <Row label="Tanggal Bergabung" value={s.join_date} />
          <Row label="PIC" value={s.assigned_to_name} />
          <Row label="Catatan" value={s.notes} />
        </div>
        {s.prospect_id && (
          <button onClick={() => navigate(`/prospek/${s.prospect_id}`)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#f97316] hover:underline" data-testid="student-prospect-link"><Link2 className="h-4 w-4" /> Lihat prospek asal</button>
        )}
      </div>
      {showEdit && <ConvertModal mode="edit" type="individu" entity={s} members={members} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </AppLayout>
  );
}
